import React, { useState, useEffect } from "react";
import { 
  Users, 
  AlertCircle, 
  Activity, 
  CheckCircle2, 
  Search, 
  Bell, 
  Monitor, 
  ChevronRight,
  Heart,
  Thermometer,
  Zap,
  Droplets,
  Plus,
  Stethoscope,
  X,
  Smartphone,
  Shield,
  FileText,
  User,
  Clock,
  MapPin,
  Video
} from "lucide-react";
import DoctorSidebar from "./DoctorSidebar";
import "./doctor.css";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase/config";
import { ref, onValue } from "firebase/database";

const DoctorDashboard = () => {
  const { currentUser } = useAuth();
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLiveMonitoring, setIsLiveMonitoring] = useState(true);
  const [patients, setPatients] = useState([]);

  useEffect(() => {
    if (!currentUser) return;

    // 1. Listen to all users to find patients
    const usersRef = ref(db, "users");
    const vitalsRef = ref(db, "vitals");

    let patientsList = [];
    let allVitals = {};

    const unsubscribeUsers = onValue(usersRef, (snapshot) => {
      const usersData = snapshot.val();
      if (usersData) {
        patientsList = Object.entries(usersData)
          .filter(([uid, data]) => data && data.role === "patient")
          .map(([uid, data]) => ({
            id: data.customId || `PT-${uid.substring(0, 5).toUpperCase()}`,
            uid: uid,
            name: data.name || "Unknown Patient",
            initials: (data.name || "U").split(" ").map(n => n[0]).join(""),
            age: data.age || 45, 
            condition: data.condition || "General Checkup",
            status: "STABLE",
            vitals: { hr: 0, spo2: 0, temp: 0, bp: "0/0" },
            device: { id: "DEV-LINK-001", name: "Medico Wearable", battery: 100, status: "Offline" },
            meds: data.meds || ["Aspirin (Sample)", "Vitamins (Sample)"],
            notes: data.notes || "Initializing oversight node...",
            monitoring: data.monitoring || "Continuous",
            lastUpdated: "Never"
          }));
      } else {
        patientsList = [];
      }
      updatePatientsWithVitals();
    });

    const unsubscribeVitals = onValue(vitalsRef, (snapshot) => {
      allVitals = snapshot.val() || {};
      updatePatientsWithVitals();
    });

    const updatePatientsWithVitals = () => {
      const updated = patientsList.map(p => {
        const v = allVitals[p.uid];
        if (v) {
          const isCritical = v.heartRate > 105 || v.spo2 < 93;
          const isWarning = v.heartRate > 90 || v.spo2 < 95 || v.temperature > 37.5;
          return {
            ...p,
            status: isCritical ? "CRITICAL" : isWarning ? "WARNING" : "STABLE",
            vitals: { 
              hr: v.heartRate, 
              spo2: v.spo2, 
              temp: v.temperature.toFixed(1), 
              bp: v.bloodPressure 
            },
            device: { ...p.device, status: "Online", battery: 85 },
            lastUpdated: "Just now"
          };
        }
        return p;
      });
      setPatients(updated);
    };

    return () => {
      unsubscribeUsers();
      unsubscribeVitals();
    };
  }, [currentUser]);

  const stats = [
    { label: "Total Patients", count: patients.length, icon: Users, color: "blue", labelText: "Active records" },
    { label: "Critical", count: patients.filter(p => p.status === "CRITICAL").length, icon: AlertCircle, color: "red", labelText: "Immediate attention" },
    { label: "Warning", count: patients.filter(p => p.status === "WARNING").length, icon: Zap, color: "orange", labelText: "Monitor closely" },
    { label: "Stable", count: patients.filter(p => p.status === "STABLE").length, icon: CheckCircle2, color: "green", labelText: "Normal vitals" }
  ];

  const filteredPatients = patients.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    p.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="dashboard-shell doctor-portal">
      <DoctorSidebar />

      <main className="dashboard-content">
        <header className="dashboard-topbar">
          <div className="topbar-left">
            <div className="title-box">
              <div className="title-icon">
                <Stethoscope size={24} className="icon-blue" />
              </div>
              <div>
                <h1>Doctor Dashboard</h1>
                <p>Monitor all patients in real-time</p>
              </div>
            </div>
          </div>

          <div className="topbar-right">
            <button 
              className={`status-toggle ${isLiveMonitoring ? "active" : ""}`}
              onClick={() => setIsLiveMonitoring(!isLiveMonitoring)}
            >
              <Monitor size={18} />
              <span>Live Monitoring</span>
            </button>
            <button className="topbar-icon-btn glass-card">
              <Search size={18} />
            </button>
            <button className="topbar-icon-btn glass-card alert">
              <Bell size={18} />
              <span className="notification-dot"></span>
            </button>
          </div>
        </header>

        <div className="scrollable-content dashboard-grid">
          {/* Stats Hub */}
          <div className="stats-hub-row">
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <div key={stat.label} className={`stat-card glass-card ${stat.color}`}>
                  <div className="stat-content">
                    <span className="stat-label">{stat.label}</span>
                    <span className="stat-count">{stat.count}</span>
                    <span className="stat-desc">{stat.labelText}</span>
                  </div>
                  <div className={`stat-icon-box ${stat.color}`}>
                    <Icon size={20} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Patient List Section */}
          <section className="patient-list-section">
            <div className="section-header">
              <div className="header-title">
                <Activity size={20} className="icon-blue" />
                <h2>Patient List</h2>
              </div>
              <div className="search-bar glass-card">
                <Search size={18} className="icon-dim" />
                <input 
                  type="text" 
                  placeholder="Search by name or ID..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="patient-list">
              {filteredPatients.map((patient) => (
                <div key={patient.id} className={`patient-row-large glass-card ${patient.status.toLowerCase()}`}>
                  <div className="patient-main-info">
                    <div className="patient-avatar-box">
                      <div className={`avatar initials ${patient.status.toLowerCase()}`}>
                        {patient.initials}
                      </div>
                    </div>
                    <div className="patient-meta">
                      <div className="name-box">
                        <h3>{patient.name}</h3>
                        <span className={`status-badge-inline ${patient.status.toLowerCase()}`}>
                          {patient.status}
                        </span>
                      </div>
                      <p className="meta-text">{patient.id} • {patient.age}y • {patient.condition}</p>
                    </div>
                  </div>

                  <div className="patient-vitals-preview">
                    <div className="vital-mini-stat">
                      <div className="mini-label"><Heart size={14} /> HR</div>
                      <div className="mini-value">{patient.vitals.hr}</div>
                    </div>
                    <div className="vital-mini-stat">
                      <div className="mini-label"><Droplets size={14} /> SpO2</div>
                      <div className={`mini-value ${patient.vitals.spo2 < 95 ? "alert" : ""}`}>
                        {patient.vitals.spo2}%
                      </div>
                    </div>
                    <div className="vital-mini-stat">
                      <div className="mini-label"><Thermometer size={14} /> Temp</div>
                      <div className="mini-value">{patient.vitals.temp}°C</div>
                    </div>
                  </div>

                  <div className="patient-device-info">
                    <div className="device-meta">
                      <Smartphone size={14} className="icon-dim" />
                      <span>{patient.device.id}</span>
                    </div>
                    <div className="device-status">
                      <span className="status-dot online"></span>
                      <span>Online • {patient.device.battery}%</span>
                    </div>
                  </div>

                  <div className="patient-actions">
                    <button 
                      className="view-details-btn premium-btn secondary"
                      onClick={() => setSelectedPatient(patient)}
                    >
                      <Monitor size={16} />
                      View Details
                    </button>
                    <span className="last-updated">{patient.lastUpdated}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Patient Details Modal */}
        {selectedPatient && (
          <div className="modal-backdrop">
            <div className="patient-details-modal glass-card">
              <div className="modal-header-large">
                <div className="patient-header-left">
                  <div className={`avatar-large initials ${selectedPatient.status.toLowerCase()}`}>
                    {selectedPatient.initials}
                  </div>
                  <div className="header-meta">
                    <div className="name-row">
                      <h2>{selectedPatient.name}</h2>
                      <span className={`status-badge-inline ${selectedPatient.status.toLowerCase()}`}>
                        {selectedPatient.status}
                      </span>
                    </div>
                    <p className="last-sync">Last updated: {selectedPatient.lastUpdated}</p>
                  </div>
                </div>
                <button className="close-modal-btn" onClick={() => setSelectedPatient(null)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body-scrollable">
                {/* Stats Grid */}
                <div className="modal-stats-grid">
                  <div className="modal-stat-box glass-card">
                    <div className="stat-icon icon-blue"><User size={18} /></div>
                    <div className="stat-text">
                      <span className="label">Patient ID</span>
                      <span className="value">{selectedPatient.id}</span>
                    </div>
                  </div>
                  <div className="modal-stat-box glass-card">
                    <div className="stat-icon icon-blue"><Clock size={18} /></div>
                    <div className="stat-text">
                      <span className="label">Age</span>
                      <span className="value">{selectedPatient.age} years</span>
                    </div>
                  </div>
                  <div className="modal-stat-box glass-card">
                    <div className="stat-icon icon-blue"><FileText size={18} /></div>
                    <div className="stat-text">
                      <span className="label">Condition</span>
                      <span className="value">{selectedPatient.condition}</span>
                    </div>
                  </div>
                  <div className="modal-stat-box glass-card">
                    <div className="stat-icon icon-blue"><Monitor size={18} /></div>
                    <div className="stat-text">
                      <span className="label">Monitoring</span>
                      <span className="value">{selectedPatient.monitoring}</span>
                    </div>
                  </div>
                </div>

                {/* Vitals HUD */}
                <section className="modal-section">
                  <div className="section-title">
                    <Activity size={18} className="icon-blue" />
                    <h3>Real-time Vitals</h3>
                  </div>
                  <div className="vitals-cards-grid">
                    <div className="vital-card-wide glass-card heart">
                      <div className="vital-header">
                        <Heart size={18} className="icon-red" />
                        <span className="status-pill normal">normal</span>
                      </div>
                      <div className="vital-body">
                        <span className="label">Heart Rate</span>
                        <div className="value-row">
                          <span className="value">{selectedPatient.vitals.hr}</span>
                          <span className="unit">bpm</span>
                        </div>
                        <span className="range">Normal: 60-100 bpm</span>
                      </div>
                    </div>

                    <div className="vital-card-wide glass-card spo2">
                      <div className="vital-header">
                        <Droplets size={18} className="icon-blue" />
                        <span className={`status-pill ${selectedPatient.vitals.spo2 < 95 ? "warning" : "normal"}`}>
                          {selectedPatient.vitals.spo2 < 95 ? "warning" : "normal"}
                        </span>
                      </div>
                      <div className="vital-body">
                        <span className="label">SpO2</span>
                        <div className="value-row">
                          <span className="value">{selectedPatient.vitals.spo2}</span>
                          <span className="unit">%</span>
                        </div>
                        <span className="range">Normal: 95-100%</span>
                      </div>
                    </div>

                    <div className="vital-card-wide glass-card temp">
                      <div className="vital-header">
                        <Thermometer size={18} className="icon-orange" />
                        <span className="status-pill normal">normal</span>
                      </div>
                      <div className="vital-body">
                        <span className="label">Temperature</span>
                        <div className="value-row">
                          <span className="value">{selectedPatient.vitals.temp}</span>
                          <span className="unit">°C</span>
                        </div>
                        <span className="range">Normal: 36.1-37.2°C</span>
                      </div>
                    </div>

                    <div className="vital-card-wide glass-card bp">
                      <div className="vital-header">
                        <Activity size={18} className="icon-purple" />
                        <span className="status-pill normal">normal</span>
                      </div>
                      <div className="vital-body">
                        <span className="label">Blood Pressure</span>
                        <div className="value-row">
                          <span className="value">{selectedPatient.vitals.bp}</span>
                          <span className="unit">mmHg</span>
                        </div>
                        <span className="range">Normal: 90/60-120/80</span>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Device Info */}
                <section className="modal-section">
                  <div className="section-title">
                    <Smartphone size={18} className="icon-blue" />
                    <h3>Device Information</h3>
                  </div>
                  <div className="device-info-bar glass-card">
                    <div className="info-item">
                      <span className="label">Device ID</span>
                      <span className="value">{selectedPatient.device.id}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">Device Name</span>
                      <span className="value">{selectedPatient.device.name}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">Connection</span>
                      <div className="status-row">
                        <Video size={14} className="icon-main" />
                        <span>Wifi</span>
                        <span className="status-pill online">Online</span>
                      </div>
                    </div>
                    <div className="info-item">
                      <span className="label">Battery</span>
                      <div className="battery-row">
                        <Zap size={14} className="icon-orange" />
                        <span className="value">{selectedPatient.device.battery}%</span>
                      </div>
                      <div className="battery-progress-bg">
                        <div 
                          className="battery-progress-fill" 
                          style={{ width: `${selectedPatient.device.battery}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Medical Summary */}
                <section className="modal-section double-col">
                  <div className="summary-col">
                    <div className="section-title">
                      <Shield size={18} className="icon-blue" />
                      <h3>Medical Summary</h3>
                    </div>
                    <div className="summary-card glass-card">
                      <h4>Current Medications</h4>
                      <ul className="meds-list">
                        {(selectedPatient.meds || []).map((med, i) => (
                          <li key={i}>• {med}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="summary-col">
                    <div className="section-title transparent">
                      <FileText size={18} className="icon-blue" />
                      <h3>Recent Notes</h3>
                    </div>
                    <div className="summary-card glass-card">
                      <h4>Recent Notes</h4>
                      <ul className="notes-list">
                        <li>• {selectedPatient.notes}</li>
                        <li>• Continue current medication regimen</li>
                        <li>• Follow-up in 2 weeks</li>
                      </ul>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DoctorDashboard;
